// src/pages/Todo/TodoPlanner.tsx — Daily Planner main page
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

import {
  DndContext,
  pointerWithin,
  rectIntersection,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
  type DragEndEvent,
  type DragStartEvent,
  type CollisionDetection,
} from "@dnd-kit/core";
import { createPortal } from "react-dom";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Sun, CalendarRange, AlertTriangle, History, Search, Filter, Sparkles, RefreshCcw,
  Users, Check, Clock, Flag, User, Briefcase, Pin, Trash2, Pencil,
  PlayCircle, MoreVertical, BarChart3, ChevronDown, TrendingUp, Zap, Award,
  ListTodo, CheckCircle2
} from "lucide-react";
import { format, addDays, parseISO, isPast, isToday } from "date-fns";

import todoApi, {
  type PlannerItem,
  type PersonalTodo,
  type AssignedItem,
  type TodoQuickAdd,
  type TodoSummary,
  type TodoUpdate
} from "../../api/todo";

import QuickAdd from "./QuickAdd";
import { useReminderStream } from "../../hooks/useReminderStream";

type Bucket = "today" | "upcoming" | "missed";

// ========== Stats Dropdown Component ==========
const StatsDropdown: React.FC<{ summary: TodoSummary; isOpen: boolean; onClose: () => void }> = ({ summary, isOpen, onClose }) => {
  const s = summary;
  const rate = Math.min(100, Math.max(0, s.completion_rate));
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <motion.div
      ref={dropdownRef}
      initial={{ opacity: 0, scale: 0.95, y: -10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -10 }}
      className="absolute top-full right-0 mt-2 w-[420px] z-50"
    >
      <div className="gt-dropdown gt-pop-in">
        {/* Header */}
        <div className="gt-cal-head" style={{ paddingTop: 14, paddingBottom: 14 }}>
          <div className="flex items-center gap-2">
            <span className="gt-leaf" />
            <span className="gt-eyebrow">i. Overview</span>
          </div>
          <span className="gt-page-sub gt-numerals">
            {format(new Date(), "MMM d, yyyy")}
          </span>
        </div>

        {/* Stats Grid */}
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="gt-stat">
              <div className="gt-stat-label"><ListTodo className="w-3.5 h-3.5" /> Today</div>
              <p className="gt-stat-value">{s.total}</p>
            </div>
            <div className="gt-stat">
              <div className="gt-stat-label"><CheckCircle2 className="w-3.5 h-3.5" /> Completed</div>
              <p className="gt-stat-value">{s.completed}</p>
              <p className="gt-stat-foot"><em className="gt-serif-italic">{rate}%</em> done</p>
            </div>
            <div className="gt-stat">
              <div className="gt-stat-label"><Clock className="w-3.5 h-3.5" /> Pending</div>
              <p className="gt-stat-value">{s.pending}</p>
            </div>
            <div className="gt-stat" style={{ borderColor: s.overdue > 0 ? 'rgba(220,38,38,0.18)' : undefined }}>
              <div className="gt-stat-label"><AlertTriangle className="w-3.5 h-3.5" /> Overdue</div>
              <p className="gt-stat-value" style={{ color: s.overdue > 0 ? '#991b1b' : undefined }}>{s.overdue}</p>
              {s.overdue > 0 && <p className="gt-stat-foot" style={{ color: '#991b1b' }}>needs attention</p>}
            </div>
          </div>

          {/* Progress Section */}
          <div style={{ borderTop: '1px dashed var(--gt-border-strong)', paddingTop: 16 }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4" style={{ color: 'var(--gt-accent-strong)' }} />
                <span className="gt-eyebrow">ii. Daily Progress</span>
              </div>
              <span className="gt-display gt-numerals" style={{ fontSize: 18 }}>{rate}<span style={{ color: 'var(--gt-text-tertiary)', fontSize: 12 }}>%</span></span>
            </div>

            {/* Pie + Bar */}
            <div className="flex items-center justify-between gap-4">
              <div className="relative w-20 h-20">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="var(--gt-surface-2)" strokeWidth="8" />
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="var(--gt-primary)"
                    strokeWidth="8"
                    strokeDasharray={2 * Math.PI * 40}
                    strokeDashoffset={2 * Math.PI * 40 * (1 - rate / 100)}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dashoffset 600ms cubic-bezier(0.4,0,0.2,1)' }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="gt-display gt-numerals" style={{ fontSize: 16, color: 'var(--gt-text-primary)' }}>{s.total}</span>
                </div>
              </div>
              <div className="flex-1">
                <div className="gt-progress-track">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${rate}%` }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className="gt-progress-fill"
                  />
                </div>
                <div className="flex justify-between mt-2 gt-page-sub gt-numerals" style={{ fontSize: 11 }}>
                  <span>{s.completed} done</span>
                  <span>{s.pending} left</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="gt-rule" />
          <div className="flex justify-between gt-page-sub gt-numerals" style={{ fontSize: 11 }}>
            <div className="flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              <span>Completion <em className="gt-serif-italic" style={{ color: 'var(--gt-accent-strong)' }}>{rate}%</em></span>
            </div>
            <div className="flex items-center gap-1">
              <Zap className="w-3 h-3" />
              <span>{rate >= 70 ? "Excellent" : rate >= 40 ? "Good" : "Keep going"}</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ========== Todo Card Component ==========
const TodoCardComponent: React.FC<{
  item: PlannerItem;
  onToggle?: (it: PersonalTodo) => void;
  onStatusChange?: (it: PersonalTodo, newStatus: string) => void;
  onOpen?: (it: PlannerItem) => void;
  onLink?: (it: AssignedItem) => void;
  onUnlink?: (it: AssignedItem) => void;
  onDelete?: (it: PersonalTodo) => void;
  onEdit?: (it: PersonalTodo, changes: TodoUpdate) => void;
  variant?: "today" | "upcoming" | "missed";
}> = ({ item, onToggle, onStatusChange, onOpen, onLink, onUnlink, onDelete, onEdit, variant = "today" }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(item.title);
  const [editPriority, setEditPriority] = useState(item.priority);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);
  const PRIO_COLORS: Record<string, string> = {
    URGENT: '#dc2626', HIGH: '#ea580c', MEDIUM: '#d97706', LOW: '#2563eb',
  };
  const isPersonal = item.source === "personal";
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    disabled: item.source !== "personal",
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    minHeight: 0,
    opacity: isDragging ? 0.25 : 1,
  };
  const isDone = isPersonal ? item.status === "COMPLETED" : item.status === "DONE";
  const isInProgress = isPersonal && item.status === "IN_PROGRESS";
  const dueDate = item.due_date ? parseISO(item.due_date) : null;
  const isOverdue = dueDate && !isDone && isPast(dueDate) && !isToday(dueDate);

  const priorityBarClass = {
    URGENT: "is-urgent",
    HIGH: "is-high",
    MEDIUM: "is-medium",
    LOW: "is-low",
  } as const;

  const priorityPillClass = {
    URGENT: "gt-pill is-urgent",
    HIGH: "gt-pill is-high",
    MEDIUM: "gt-pill is-medium",
    LOW: "gt-pill is-low",
  } as const;

  const statusPillClass: Record<string, string> = {
    PENDING: "gt-pill is-pending",
    IN_PROGRESS: "gt-pill is-progress",
    COMPLETED: "gt-pill is-done",
    DONE: "gt-pill is-done",
  };

  const handleCheckboxClick = (e) => {
    e.stopPropagation();

    if (!isPersonal || !onStatusChange) return;

    if (item.status === "COMPLETED") {
      // uncheck → back to pending
      onStatusChange(item, "PENDING");
    } else {
      // first click → complete
      onStatusChange(item, "COMPLETED");
    }
  };


  const handleInProgress = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPersonal && onStatusChange && item.status !== "IN_PROGRESS") {
      onStatusChange(item, "IN_PROGRESS");
    }
  };

  const handleEditSave = async () => {
    if (isPersonal && onEdit) {
      await onEdit(item, { title: editTitle, priority: editPriority as any });
      setIsEditing(false);
    }
  };

  return (
    <div ref={setNodeRef} style={style} {...(isPersonal ? attributes : {})} {...(isPersonal ? listeners : {})}>
    <div
      className={`group gt-task-card overflow-visible is-prio-${(item.priority || 'medium').toLowerCase()} ${isDone ? "is-done" : ""} ${isOverdue ? "is-overdue" : ""}`}
      style={{ minHeight: 0, cursor: isPersonal ? 'grab' : 'pointer', touchAction: isPersonal ? 'none' : 'auto' }}
      onClick={() => onOpen?.(item)}
    >
      <div className={`gt-prio-bar ${priorityBarClass[item.priority as keyof typeof priorityBarClass]}`} />

      <div className="p-3 pl-4 pr-10">
        {/* Compact title row — drag indicator + checkbox + title */}
        <div className="flex items-center gap-2 mb-1.5">
          {isPersonal && (
            <div
              className="shrink-0 p-1 -m-1"
              style={{ color: 'var(--gt-text-tertiary)', pointerEvents: 'none' }}
              aria-hidden
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h.01M8 12h.01M8 18h.01M16 6h.01M16 12h.01M16 18h.01" />
              </svg>
            </div>
          )}
          {isPersonal ? (
            <button
              onClick={handleCheckboxClick}
              className="shrink-0 flex items-center justify-center"
              style={{
                width: 18, height: 18,
                borderRadius: 999,
                border: `2px solid ${isDone ? 'var(--gt-accent-strong)' : isInProgress ? '#3b82f6' : 'var(--gt-border-strong)'}`,
                background: isDone ? 'var(--gt-accent-strong)' : isInProgress ? '#dbeafe' : 'transparent',
                color: isDone ? '#fff' : isInProgress ? '#1e40af' : 'var(--gt-primary)',
                transition: 'all 200ms cubic-bezier(0.33, 1, 0.68, 1)',
              }}
            >
              {isDone && <Check className="w-2.5 h-2.5" strokeWidth={3} />}
              {isInProgress && !isDone && <PlayCircle className="w-2.5 h-2.5" />}
            </button>
          ) : (
            <div className="shrink-0 flex items-center justify-center"
              style={{ width: 18, height: 18, borderRadius: 999, background: 'var(--gt-surface-2)', color: 'var(--gt-text-tertiary)' }}>
              <Briefcase className="w-2.5 h-2.5" />
            </div>
          )}
          {!isEditing && (
            <h3 className="gt-display flex-1 min-w-0 line-clamp-1 group-hover:line-clamp-none transition-all duration-200" style={{
              fontSize: 14, fontWeight: 600,
              color: isDone ? 'var(--gt-text-disabled)' : 'var(--gt-text-primary)',
              textDecoration: isDone ? 'line-through' : 'none',
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}>
              {item.title}
            </h3>
          )}
        </div>

        {/* Full-width content area */}
        <div className="flex">
          <div className="flex-1 min-w-0 space-y-1">
            {isEditing ? (
              <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                <input
                  autoFocus
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="gt-input"
                />
                <Select
                  value={editPriority}
                  onValueChange={(v) => setEditPriority(v as any)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="URGENT">Urgent</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="MEDIUM">Medium</SelectItem>
                    <SelectItem value="LOW">Low</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <button onClick={handleEditSave} className="gt-btn is-primary is-sm">Save</button>
                  <button onClick={() => setIsEditing(false)} className="gt-btn is-ghost is-sm">Cancel</button>
                </div>
              </div>
            ) : (
              <>
                {/* Description / notes — kept visible in BOTH states */}
                {item.description && (
                  <p className="line-clamp-2 group-hover:line-clamp-none transition-all duration-200" style={{
                    fontSize: 12,
                    color: isDone ? 'var(--gt-text-disabled)' : 'var(--gt-text-tertiary)',
                  }}>
                    {item.description}
                  </p>
                )}

                {isDone ? (
                  <div className="mt-2.5" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#16a34a', fontWeight: 700 }}>
                    <Check className="w-3.5 h-3.5" /> Completed
                  </div>
                ) : (
                  <>
                    {/* Inline metadata row — informational text, not buttons */}
                    <div style={{
                      display: 'flex', alignItems: 'center', flexWrap: 'wrap',
                      gap: '6px 10px', marginTop: 8, fontSize: 11.5,
                      color: 'var(--gt-text-tertiary)', fontWeight: 500,
                    }}>
                      {isPersonal && (item as any).source_task_id && (
                        <>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#9a3412', fontWeight: 600 }} title="Created from a task checklist">
                            <Briefcase className="w-3 h-3" /> From Task
                          </span>
                          <span style={{ color: 'var(--gt-border-strong)' }}>·</span>
                        </>
                      )}
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {isPersonal ? <User className="w-3 h-3" /> : <Briefcase className="w-3 h-3" />}
                        {isPersonal ? 'Personal' : 'Assigned'}
                      </span>
                      <span style={{ color: 'var(--gt-border-strong)' }}>·</span>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        color: PRIO_COLORS[item.priority] || PRIO_COLORS.MEDIUM,
                        fontWeight: 700, letterSpacing: '0.02em',
                      }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'currentColor', flexShrink: 0 }} />
                        {item.priority}
                      </span>
                      <span style={{ color: 'var(--gt-border-strong)' }}>·</span>
                      <span style={{ textTransform: 'capitalize' }}>
                        {item.status.replace(/_/g, " ").toLowerCase()}
                      </span>
                      {dueDate && (
                        <>
                          <span style={{ color: 'var(--gt-border-strong)' }}>·</span>
                          <span className="gt-numerals" style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            color: isOverdue ? '#b91c1c' : 'var(--gt-text-tertiary)',
                          }}>
                            <Clock className="w-3 h-3" />
                            {format(dueDate, "MMM d")}
                          </span>
                        </>
                      )}
                    </div>

                    {/* Action Buttons — only In Progress + Complete */}
                    {isPersonal && (
                      <div className={`grid ${isInProgress ? 'grid-cols-1' : 'grid-cols-2'} gap-2 mt-3`}>
                        {!isInProgress && onStatusChange && (
                          <button
                            onClick={handleInProgress}
                            className="gt-btn"
                            style={{
                              justifyContent: 'center', width: '100%', padding: '9px 14px',
                              background: '#dbeafe', color: '#1e40af',
                              border: '1px solid transparent', fontWeight: 600,
                            }}
                          >
                            <PlayCircle className="w-4 h-4" /> In Progress
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onStatusChange?.(item, "COMPLETED");
                          }}
                          className="gt-btn"
                          style={{
                            justifyContent: 'center', width: '100%', padding: '9px 14px',
                            background: '#16a34a', color: '#ffffff', border: '1px solid transparent', fontWeight: 700,
                            boxShadow: '0 1px 2px rgba(22, 163, 74, 0.25)',
                          }}
                        >
                          <Check className="w-4 h-4" /> Complete
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Action Icons */}
          <div className="absolute top-2 right-2 flex items-center gap-1">
            {!isPersonal && variant === "today" && (
              item.link_id ? (
                <button onClick={(e) => { e.stopPropagation(); onUnlink?.(item); }} className="gt-icon-btn is-sm opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--gt-dnd)' }}>
                  <Pin className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button onClick={(e) => { e.stopPropagation(); onLink?.(item); }} className="gt-icon-btn is-sm opacity-0 group-hover:opacity-100 transition-opacity">
                  <Pin className="w-3.5 h-3.5" />
                </button>
              )
            )}

            {isPersonal && (onEdit || onDelete) && !isEditing && (
              showDeleteConfirm ? (
                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => { onDelete?.(item); setShowDeleteConfirm(false); }} className="gt-btn is-danger is-sm">Delete</button>
                  <button onClick={() => setShowDeleteConfirm(false)} className="gt-btn is-ghost is-sm">Cancel</button>
                </div>
              ) : (
                <div ref={menuRef} style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v); }}
                    className="gt-icon-btn is-sm"
                    aria-label="More actions"
                    title="More actions"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                  {menuOpen && (
                    <div
                      className="gt-dropdown"
                      style={{
                        position: 'absolute', right: 0, top: '110%',
                        minWidth: 140, padding: 6, zIndex: 50,
                      }}
                    >
                      {onEdit && (
                        <button
                          onClick={() => { setMenuOpen(false); setIsEditing(true); }}
                          className="gt-dropdown-item"
                          style={{ borderRadius: 8 }}
                        >
                          <Pencil className="w-4 h-4" /> Edit
                        </button>
                      )}
                      {onDelete && (
                        <button
                          onClick={() => { setMenuOpen(false); setShowDeleteConfirm(true); }}
                          className="gt-dropdown-item"
                          style={{ color: 'var(--gt-dnd)', borderRadius: 8 }}
                        >
                          <Trash2 className="w-4 h-4" /> Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

// ========== Main TodoPlanner Component ==========
export default function TodoPlanner() {
  const navigate = useNavigate();
  const [todayItems, setTodayItems] = useState<PlannerItem[]>([]);
  const [upcomingItems, setUpcomingItems] = useState<PlannerItem[]>([]);
  const [missedItems, setMissedItems] = useState<PlannerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<TodoSummary | null>(null);
  const [search, setSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState<string>("ALL");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [activeDrag, setActiveDrag] = useState<PlannerItem | null>(null);
  const [showStats, setShowStats] = useState(false);
  const { user } = useAuth();
  const userRole = user?.role;

  const navigateToTask = useCallback((id: string | null) => {
    if (id) navigate(`/tasks/${id}`);
  }, [navigate]);
  useReminderStream(navigateToTask);

  const refreshData = useCallback(async () => {
    setLoading(true);
    try {
      const [todayRes, upcomingRes, missedRes, summaryRes] = await Promise.all([
        todoApi.today(),
        todoApi.upcoming(30),
        todoApi.missed(),
        todoApi.summary()
      ]);

      const mergeItems = (personal: PersonalTodo[], assigned: AssignedItem[]) => [...personal, ...assigned];
      setTodayItems(mergeItems(todayRes.data.personal || [], todayRes.data.assigned || []));
      setUpcomingItems(mergeItems(upcomingRes.data.personal || [], upcomingRes.data.assigned || []));
      setMissedItems(mergeItems(missedRes.data.personal || [], missedRes.data.assigned || []));
      setSummary(summaryRes.data);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const filterItems = useCallback((items: PlannerItem[]) => {
    return items.filter(item => {
      const matchesSearch = !search || item.title.toLowerCase().includes(search.toLowerCase());
      const matchesPriority = priorityFilter === "ALL" || item.priority === priorityFilter;
      const matchesStatus = statusFilter === "ALL" || item.status === statusFilter;
      return matchesSearch && matchesPriority && matchesStatus;
    });
  }, [search, priorityFilter, statusFilter]);

  const filteredToday = useMemo(() => filterItems(todayItems), [todayItems, filterItems]);
  const filteredUpcoming = useMemo(() => filterItems(upcomingItems), [upcomingItems, filterItems]);
  const filteredMissed = useMemo(() => filterItems(missedItems), [missedItems, filterItems]);

  const handleQuickAdd = async (payload: TodoQuickAdd) => {
    await todoApi.quickAdd(payload);
    refreshData();
  };

  const handleToggle = async (item: PersonalTodo) => {
    await todoApi.toggle(item.id);
    refreshData();
    toast.success(item.status === "COMPLETED" ? "Task reopened" : "Task completed! 🎉");
  };

  const handleStatusChange = async (item: PersonalTodo, newStatus: string) => {
    await todoApi.update(item.id, { status: newStatus as any });
    refreshData();
    toast.success(`Status updated to ${newStatus.replace(/_/g, " ")}`);
  };

  const handleDelete = async (item: PersonalTodo) => {
    await todoApi.remove(item.id);
    refreshData();
    toast.success("Task deleted");
  };

  const handleEdit = async (item: PersonalTodo, changes: TodoUpdate) => {
    await todoApi.update(item.id, changes);
    refreshData();
  };

  const handleLink = async (item: AssignedItem) => {
    await todoApi.linkTask(item.id);
    refreshData();
    toast.success("Task pinned to today");
  };

  const handleUnlink = async (item: AssignedItem) => {
    if (item.link_id) {
      await todoApi.unlink(item.link_id);
      refreshData();
      toast.success("Task unpinned");
    }
  };

  const handleOpen = (item: PlannerItem) => {
    if (item.source === "assigned") navigate(`/tasks/${item.id}`);
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  // Custom collision: prefer pointerWithin (catches column droppables), fallback to rectIntersection
  const collisionDetection: CollisionDetection = (args) => {
    const pointer = pointerWithin(args);
    if (pointer.length > 0) return pointer;
    return rectIntersection(args);
  };

  const onDragStart = (e: DragStartEvent) => {
    const id = String(e.active.id);
    const item = [...todayItems, ...upcomingItems, ...missedItems].find(t => t.id === id);
    if (item?.source === "personal") setActiveDrag(item);
  };

  const findBucket = (id: string): Bucket | null => {
    if (id === "today" || id === "upcoming" || id === "missed") return id as Bucket;
    if (todayItems.some(t => t.id === id)) return "today";
    if (upcomingItems.some(t => t.id === id)) return "upcoming";
    if (missedItems.some(t => t.id === id)) return "missed";
    return null;
  };

  const bucketSetter = (b: Bucket) =>
    b === "today" ? setTodayItems : b === "upcoming" ? setUpcomingItems : setMissedItems;
  const bucketItems = (b: Bucket): PlannerItem[] =>
    b === "today" ? todayItems : b === "upcoming" ? upcomingItems : missedItems;

  const onDragEnd = async (e: DragEndEvent) => {
    setActiveDrag(null);
    const { active, over } = e;
    if (!over) return;

    const taskId = String(active.id);
    const overId = String(over.id);
    if (taskId === overId) return;

    const sourceBucket = findBucket(taskId);
    const targetBucket = findBucket(overId);
    if (!sourceBucket || !targetBucket) return;

    // Same column → reorder locally (visual only; backend ordering not persisted yet)
    if (sourceBucket === targetBucket) {
      const items = bucketItems(sourceBucket);
      const oldIdx = items.findIndex(t => t.id === taskId);
      const newIdx = items.findIndex(t => t.id === overId);
      if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) return;
      bucketSetter(sourceBucket)(arrayMove(items, oldIdx, newIdx));
      return;
    }

    // Cross-column → reschedule and refetch
    const targetDate = (() => {
      const today = new Date();
      if (targetBucket === "today") return format(today, "yyyy-MM-dd");
      if (targetBucket === "upcoming") return format(addDays(today, 1), "yyyy-MM-dd");
      return format(addDays(today, -1), "yyyy-MM-dd");
    })();

    // Optimistic move
    const moving = bucketItems(sourceBucket).find(t => t.id === taskId);
    bucketSetter(sourceBucket)(prev => prev.filter(t => t.id !== taskId));
    if (moving) {
      bucketSetter(targetBucket)(prev => [{ ...moving, due_date: targetDate } as PlannerItem, ...prev]);
    }

    try {
      await todoApi.reschedule(taskId, targetDate);
      toast.success(`Task moved to ${targetBucket === "today" ? "Today" : targetBucket === "upcoming" ? "Upcoming" : "Missed"}`);
      refreshData();
    } catch {
      toast.error("Failed to move task");
      refreshData();
    }
  };

  const Column = ({ id, title, icon, items, variant, accent }: {
    id: Bucket;
    title: string;
    icon: React.ReactNode;
    items: PlannerItem[];
    variant: "today" | "upcoming" | "missed";
    accent: string;
  }) => {
    const { setNodeRef, isOver } = useDroppable({ id });
    const personalItems = items.filter(i => i.source === "personal");

    const tileVariant = id === 'today' ? 'is-info' : id === 'upcoming' ? 'is-warm' : 'is-alert';
    return (
      <div className={`gt-column flex flex-col ${isOver ? "is-over" : ""}`}>
        <div className="gt-column-head">
          <div className={`gt-icon-tile ${tileVariant}`}>
            {icon}
          </div>
          <div className="flex flex-col">
            <span className="gt-eyebrow">{id === 'today' ? 'i.' : id === 'upcoming' ? 'ii.' : 'iii.'}</span>
            <h2 className="gt-display" style={{ fontSize: 16, color: 'var(--gt-text-primary)', lineHeight: 1, marginTop: 1 }}>{title}</h2>
          </div>
          <span className="gt-count gt-numerals">{String(items.length).padStart(2, '0')}</span>
        </div>

        <div ref={setNodeRef} className="p-3 space-y-2.5 min-h-[600px] max-h-[75vh] overflow-y-auto gt-scroll">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="gt-loading-ring" />
            </div>
          ) : items.length === 0 ? (
            <div className="gt-empty">
              <div className="gt-empty-leaf"><span className="gt-leaf" /></div>
              <div className="gt-empty-title">Nothing here</div>
              <div className="gt-page-sub">A clear column is a <em className="gt-serif-italic">good thing</em>.</div>
            </div>
          ) : (
            <SortableContext items={personalItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
              {items.map(item => (
                <TodoCardComponent
                  key={item.id}
                  item={item}
                  onStatusChange={handleStatusChange}
                  onOpen={handleOpen}
                  onLink={handleLink}
                  onUnlink={handleUnlink}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                  variant={variant}
                />
              ))}
            </SortableContext>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="gt-shell min-h-screen">
      <div className="w-full px-4 md:px-7 py-4 md:py-7 space-y-6 gt-fade-in">
        {/* Header — title left, inline overview stats + employee planner right */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="gt-page-hero flex-col md:flex-row gap-6">
          <div>
            <div className="gt-eyebrow-row">
              <span className="gt-leaf" />
              <span className="gt-eyebrow">i. Today</span>
              <span style={{ color: 'var(--gt-text-tertiary)', fontSize: 12 }}>·</span>
              <span className="gt-page-sub gt-numerals">{format(new Date(), "EEEE")}</span>
            </div>
            <h1 className="gt-page-title">
              Daily <em>Planner</em>
            </h1>
            <p className="gt-page-sub gt-numerals" style={{ marginTop: 6 }}>
              {format(new Date(), "MMMM d, yyyy")}
            </p>
          </div>

          <div className="flex flex-col items-stretch md:items-end gap-3 md:flex-1 md:max-w-[640px]">
            {userRole !== "employee" && (
              <button
                onClick={() => navigate("/tasks/employee-tasks")}
                className="gt-btn is-primary self-end"
              >
                <Users className="w-4 h-4" />
                Employee Planner
              </button>
            )}

            {/* Inline overview stats — colour-coded per status */}
            {summary && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full">
                {[
                  { k: "today", label: "Today", value: summary.total, icon: <ListTodo className="w-3.5 h-3.5" />, bg: '#eff6ff', border: '#bfdbfe', iconBg: '#dbeafe', iconFg: '#1d4ed8', label_c: '#1e40af', value_c: '#1e3a8a' },
                  { k: "completed", label: "Completed", value: summary.completed, icon: <CheckCircle2 className="w-3.5 h-3.5" />, bg: '#f0fdf4', border: '#86efac', iconBg: '#bbf7d0', iconFg: '#047857', label_c: '#047857', value_c: '#064e3b' },
                  { k: "pending", label: "Pending", value: summary.pending, icon: <Clock className="w-3.5 h-3.5" />, bg: '#fffbeb', border: '#fde68a', iconBg: '#fef3c7', iconFg: '#b45309', label_c: '#92400e', value_c: '#78350f' },
                  { k: "overdue", label: "Overdue", value: summary.overdue, icon: <AlertTriangle className="w-3.5 h-3.5" />, bg: '#f1f5f9', border: '#cbd5e1', iconBg: '#e2e8f0', iconFg: '#475569', label_c: '#475569', value_c: '#1e293b' },
                ].map((s) => (
                  <div key={s.k} style={{
                    background: s.bg,
                    border: `1px solid ${s.border}`,
                    borderRadius: 14,
                    padding: '10px 14px',
                    boxShadow: 'var(--gt-elev-1)',
                  }}>
                    <div className="flex items-center gap-2" style={{ marginBottom: 2 }}>
                      <span style={{
                        width: 22, height: 22, borderRadius: 7,
                        background: s.iconBg, color: s.iconFg,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>{s.icon}</span>
                      <span className="gt-eyebrow" style={{ marginBottom: 0, color: s.label_c, fontSize: 9.5 }}>{s.label}</span>
                    </div>
                    <p className="gt-display gt-numerals" style={{
                      fontSize: 22, fontWeight: 800, letterSpacing: '-0.025em',
                      color: s.value_c, lineHeight: 1, margin: 0,
                    }}>{s.value}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {/* Quick Add */}
        <QuickAdd onCreate={handleQuickAdd} />

        {/* Filters — single row: search · priority · status · history */}
        <div className="flex flex-nowrap items-center gap-3 overflow-x-auto">
          <div className="flex-1 min-w-[240px] gt-search-wrap">
            <Search />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tasks…"
              className="gt-input is-search"
            />
          </div>

          <div className="w-[160px] flex-shrink-0">
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Priorities</SelectItem>
                <SelectItem value="URGENT">Urgent</SelectItem>
                <SelectItem value="HIGH">High</SelectItem>
                <SelectItem value="MEDIUM">Medium</SelectItem>
                <SelectItem value="LOW">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="w-[160px] flex-shrink-0">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Status</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <button
            onClick={() => navigate("/todo/history")}
            className="gt-btn is-ghost"
            style={{ flexShrink: 0 }}
          >
            <History className="w-4 h-4" />
            View Task History
          </button>
        </div>

        {/* Three Columns */}
        <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Column id="today" title="Today" icon={<Sun className="w-4 h-4" />} items={filteredToday} variant="today" accent="" />
            <Column id="upcoming" title="Upcoming" icon={<CalendarRange className="w-4 h-4" />} items={filteredUpcoming} variant="upcoming" accent="" />
            <Column id="missed" title="Missed" icon={<AlertTriangle className="w-4 h-4" />} items={filteredMissed} variant="missed" accent="" />
          </div>
          {createPortal(
            <DragOverlay dropAnimation={null}>
              {activeDrag ? (
                <div style={{ width: 360, opacity: 0.95, cursor: 'grabbing', boxShadow: '0 18px 38px -8px rgba(15,23,42,0.28)' }}>
                  <TodoCardComponent
                    item={activeDrag}
                    onStatusChange={handleStatusChange}
                    onOpen={handleOpen}
                    onLink={handleLink}
                    onUnlink={handleUnlink}
                    variant="today"
                  />
                </div>
              ) : null}
            </DragOverlay>,
            document.body
          )}
        </DndContext>


      </div>
    </div>
  );
}